import { IpaType } from "../phonemes/ipa";
import { checkSyllableContext, SyllableContext } from "./checkSyllableContext";
import { isMatch, MatchResult } from "./isMatch";
import { Pattern } from "./pattern";
import { PhonemeInfo } from "./phonemeInfo";
import { getReplacement } from "./replace";

type Ignore = (item: string) => boolean;

/** a list of IPA phonemes and whether they were replaced */
export interface MatchedIpa {
  ipa: IpaType[];
  replaced: boolean[];  /** so we only replace a symbol once */
  skip?: boolean[];     /** items to not replace */
}

export interface ToMatch {
  pattern: Pattern<PhonemeInfo>;
  previous?: Pattern<PhonemeInfo>;
  next?: Pattern<PhonemeInfo>;
  syllable?: SyllableContext;
}

/** optional action to take after successful phoneme replacement */
export type PostActionType = 'swapWithNext' | 'swapWithPrev';

/**
 * for any matches of 'toMatch' in 'ipa', replace pattern with 'replacement',
 * anything ignorable gets copied as-is,
 * skips anything that was already replaced
 * @param input the ipa to operate on + anything previously replaced that should be skipped
 * @param toMatch  the `pattern` to find/replace, plus optional context
 * @param replacement what to replace `pattern` with if it matches
 * @param ignore pass a function that returns `false` for strings that should be ignored
 * @param post action to perform after each successful replacement
 */
export function searchAndReplace(
  input: IpaType[] | MatchedIpa,
  toMatch: ToMatch,
  replacement: IpaType[],
  ignore?: Ignore,
  post?: PostActionType,
): MatchedIpa {
  const ipaIn = Array.isArray(input) ? input : input.ipa;
  const replacedIn = Array.isArray(input) ? [] : input.replaced;
  const skipIn = Array.isArray(input) || !input.skip ? null : input.skip;
  const output: IpaType[] = [];
  const replaced: boolean[] = [];
  const skip: boolean[] = [];
  const indices: number[] = []; // if there's a post-action, track replacement indices

  const inLength = ipaIn.length;
  let iOut = 0;
  for (let iIn = 0; iIn < inLength; iIn++) {
    if (replacedIn[iIn] || (skipIn && skipIn[iIn])) {
      // was already replaced, just copy across
      output[iOut++] = ipaIn[iIn];
      replaced.push(replacedIn[iIn]);
      if (skipIn) { skip.push(skipIn[iIn]); }
    } else if (ignore !== undefined && ignore(ipaIn[iIn])) {
      const check = isContextMatch(toMatch, ipaIn, iIn); // without 'ignore'
      if (check === false) {
        // not something to try matching, just copy across
        output[iOut++] = ipaIn[iIn];
        replaced.push(replacedIn[iIn] ?? false);
        if (skipIn) { skip.push(skipIn[iIn]); }
      } else {
        // the rule modifies something we would otherwise ignore
        if (post) {
          indices.push(output.length);
        }
        const [countRemoved, newPiece] = getReplacement(ipaIn, iIn, check.count, replacement);
        newPiece.forEach(p => {
          output.push(p);
          replaced.push(ignore ? !ignore(p) : true);
          if (skipIn) { skip.push(skipIn[iIn]); }
        });
        iIn += countRemoved - 1;
        iOut += newPiece.length;
      }
    } else {
      const check = isContextMatch(toMatch, ipaIn, iIn, ignore);
      if (check === false || alreadyReplaced(replacedIn, iIn, iIn + check.count)) {
        output[iOut++] = ipaIn[iIn];
        replaced.push(false);
        if (skipIn) { skip.push(skipIn[iIn]); }
      } else {
        if (post) {
          indices.push(output.length);
        }
        replacement.forEach(p => {
          output.push(p);
          replaced.push(true);
          if (skipIn) { skip.push(false); }
        });
        iIn += check.count - 1;
        iOut += replacement.length;
      }
    }
  }

  if (post && indices.length > 0) {
    const [ipa, result] = doPostAction(post, indices, replacement.length, output, replaced);
    return skipIn ? { ipa, replaced: result, skip } : { ipa, replaced: result };
  }
  return skipIn ? { ipa: output, replaced, skip } : { ipa: output, replaced };
}

/** does ipa[i] match toMatch, including the pre & post context? */
function isContextMatch(
  toMatch: ToMatch, ipa: IpaType[], i: number, ignore?: Ignore
): MatchResult | false {
  const check = isMatch(toMatch.pattern, ipa, i, ignore);
  if (check === false) {
    return false;
  }

  if (toMatch.next && isMatch(toMatch.next, ipa, i + check.count, ignore) === false) {
    return false;
  }

  // try previous item & two previous items
  if (toMatch.previous) {
    const [result, j] = check1(toMatch.previous, ipa, i, ignore);
    if (result === false && !check2(toMatch.previous, ipa, j - 2, ignore)) {
      return false;
    }
  }

  if (toMatch.syllable && !checkSyllableContext(ipa, i, toMatch.syllable)) {
    return false;
  }

  return check;
}

/** check the previous item, taking 'ignore' into account if needed */
function check1(
  previous: Pattern<PhonemeInfo>, ipa: IpaType[], i: number, ignore?: Ignore
): [MatchResult | false, number] {
  let j = i;
  let result = isMatch(previous, ipa, j - 1, ignore);
  while (!result && ignore && ignore(ipa[j - 1])) {
    // if no match but we can ignore this one, try the one before it
    j--;
    result = isMatch(previous, ipa, j - 1, ignore);
  }
  return [result, j];
}

// the case where i-2 matches 1 item & i-1 can be ignored
function check2(
  previous: Pattern<PhonemeInfo>, ipa: IpaType[], i: number, ignore?: Ignore
): boolean {
  if ('special' in previous && previous.special === 'word' && i < -1) {
    return true;
  }
  const check = isMatch(previous, ipa, i, ignore);
  if (check !== false && check.count === 2) {
    return true;
  }
  return check !== false && check.count === 1 && ignore !== undefined && ignore(ipa[i + 1]);
}

/** check if anything from (iIn+1, end) has already been replaced */
function alreadyReplaced(replacedIn: boolean[], iIn: number, end: number): boolean {
  for (let i = iIn + 1; i < end; i++) {
    if (replacedIn[i]) {
      return true;
    }
  }
  return false;
}


/** apply any post-replacement actions, like swapping phoneme order */
function doPostAction(
  post: PostActionType, indices: number[], len: number, output: string[], replaced: boolean[]
): [string[], boolean[]] {
  if (post !== 'swapWithNext' && post !== 'swapWithPrev') {
    return [output, replaced];
  }

  const newOutput: string[] = [];
  const newReplaced: boolean[] = [];
  let lastI = 0;
  if (post === 'swapWithNext') {
    for (const idx of indices) {
      // from last to current
      newOutput.push(...output.slice(lastI, idx));
      newReplaced.push(...replaced.slice(lastI, idx));
      // move up next phoneme
      if (idx + len < output.length) {
        newOutput.push(output[idx + len]);
        newReplaced.push(replaced[idx + len]);
      }
      // then current phoneme
      newOutput.push(...output.slice(idx, idx + len));
      newReplaced.push(...replaced.slice(idx, idx + len));
      lastI = idx + len + 1;
    }
  } else if (post === 'swapWithPrev') {
    for (const idx of indices) {
      // from last to previous phoneme
      if (idx > 0) {
        newOutput.push(...output.slice(lastI, idx - 1));
        newReplaced.push(...replaced.slice(lastI, idx - 1));
      }
      // move current phoneme back
      newOutput.push(...output.slice(idx, idx + len));
      newReplaced.push(...replaced.slice(idx, idx + len));
      // then previous phoneme
      if (idx > 0) {
        newOutput.push(output[idx - 1]);
        newReplaced.push(replaced[idx - 1]);
      }
      lastI = idx + len;
    }
  }
  // tack on final slice if needed
  newOutput.push(...output.slice(lastI));
  newReplaced.push(...replaced.slice(lastI));
  return [newOutput, newReplaced];
}
